import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Divider, Paper, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import CustomerDetailsPanel from '@/shared/components/panels/CustomerDetailsPanel';
import WaveUICustomersSearchBar from '@/shared/components/widgets/WaveUICustomersSearchBar';
import OrderCustomer from '@/shared/types/order/orderCustomer.type';

import AddressExpandablePanel from './components/AddressExpandablePanel';

type CustomerDetailsProps = {
  searchCustomersList: OrderCustomer[];
  selectedCustomer: OrderCustomer[];
  selectedCustomerSnapshot: OrderCustomer[];
  searchCustomerValue: string;
  isBillingSameAsDelivery: boolean;
  customerAddressFormErrors: Record<string, string>;
  onValidateCustomerAddressForm: (
    fieldName: string,
    useSnapshot?: boolean,
  ) => void;
  setIsBillingSameAsDelivery: (value: boolean) => void;
  setSearchCustomerValue: (value: string) => void;
  setSelectedCustomerId: (value: string) => void;
  setSelectedCustomer: (value: OrderCustomer[]) => void;
  onEditCustomer: () => void;
  onCreateCustomer: () => void;
};

const CustomerDetails: FC<CustomerDetailsProps> = ({
  searchCustomersList,
  selectedCustomer,
  selectedCustomerSnapshot,
  searchCustomerValue,
  isBillingSameAsDelivery,
  customerAddressFormErrors,
  onValidateCustomerAddressForm,
  setIsBillingSameAsDelivery,
  setSearchCustomerValue,
  setSelectedCustomerId,
  setSelectedCustomer,
  onEditCustomer,
  onCreateCustomer,
}) => {
  const { t } = useTranslation(['orders', 'customers', 'common']);

  const [isFocused, setIsFocused] = useState(false);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant='body1' sx={{ mb: 1 }}>
        {t('orders:customer')}
      </Typography>

      <Stack
        direction='row'
        spacing={1}
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
        }}
      >
        <WaveUICustomersSearchBar
          searchCustomersList={searchCustomersList}
          searchCustomerValue={searchCustomerValue}
          setSearchCustomerValue={setSearchCustomerValue}
          setSelectedCustomerId={setSelectedCustomerId}
          getFocusStatus={setIsFocused}
        />

        <Button
          onClick={onCreateCustomer}
          variant='text'
          size='small'
          sx={{
            transition: 'all 0.3s',
            overflow: 'hidden',
            textWrap: 'nowrap',

            ...(isFocused
              ? {
                  width: 0,
                  fontSize: 0,
                  minWidth: 0,
                  opacity: 0,
                }
              : null),
          }}
        >
          {t('customers:createCustomer')}
        </Button>
      </Stack>

      {selectedCustomer.length > 0 && (
        <>
          <Divider sx={{ mt: 2, mx: -2 }} />

          <CustomerDetailsPanel
            customer={selectedCustomer[0]}
            isBillingAddressVisible
            isShippingAddressVisible
            paperProps={{
              elevation: 0,
              sx: { pt: 3, px: 0, mx: -2 },
            }}
            onEditCustomer={onEditCustomer}
            addressCustomRender={() => (
              <>
                <AddressExpandablePanel
                  customer={selectedCustomer[0]}
                  selectedCustomerSnapshot={selectedCustomerSnapshot[0]}
                  title={t('customers:billingAddress')}
                  reason='billing'
                  customerAddressFormErrors={customerAddressFormErrors}
                  onValidateCustomerAddressForm={onValidateCustomerAddressForm}
                  setSelectedCustomer={setSelectedCustomer}
                />

                <Box px={2}>
                  <WaveUIForm_V2
                    fields={[
                      {
                        field_type: FieldType.Checkbox,
                        field_props: {
                          name: 'isBillingSameAsDelivery',
                          label: t('orders:isBillingSameAsDelivery'),
                          checked: isBillingSameAsDelivery,
                        },
                      },
                    ]}
                    getFieldsValues={(value) => {
                      setIsBillingSameAsDelivery(
                        value.isBillingSameAsDelivery as boolean,
                      );
                    }}
                  />
                </Box>

                {!isBillingSameAsDelivery && (
                  <AddressExpandablePanel
                    customer={selectedCustomer[0]}
                    selectedCustomerSnapshot={selectedCustomerSnapshot[0]}
                    title={t('customers:shippingAddress')}
                    reason='delivery'
                    customerAddressFormErrors={customerAddressFormErrors}
                    onValidateCustomerAddressForm={
                      onValidateCustomerAddressForm
                    }
                    setSelectedCustomer={setSelectedCustomer}
                  />
                )}
              </>
            )}
          />
        </>
      )}
    </Paper>
  );
};

export default CustomerDetails;
